import { useCallback, useEffect, useMemo, useState } from 'react'
import { addDoc, collection, doc, onSnapshot, orderBy, query, serverTimestamp, updateDoc, where, writeBatch } from 'firebase/firestore'
import { db } from '../config/firebase.js'
import { useAuth } from './useAuth.js'

/**
 * Firebase v11+ Device Alerts Hook
 * 
 * Efficiently manages device health monitoring and alerting with optimized re-renders
 * and Firebase v11+ patterns. Tracks connectivity, performance, and health status.
 * 
 * Features:
 * - React 19+ optimized with minimal re-renders
 * - Firebase v11+ server timestamps and batch operations
 * - Memoized query generation for performance
 * - Comprehensive alert type helpers
 * - Real-time alert monitoring with proper cleanup
 * 
 * @param {string|null} deviceId - Specific device ID to monitor (null for all devices)
 * @returns {Object} Alert state and management functions
 */
export function useDeviceAlerts(deviceId = null) {
  const [alerts, setAlerts] = useState([])
  const [loading, setLoading] = useState(true)
  const { user } = useAuth()

  // Memoize unread count to prevent unnecessary recalculations
  const unreadCount = useMemo(() => 
    alerts.filter(alert => !alert.read).length, 
    [alerts],
  )

  // Memoize query configuration for performance
  const queryConfig = useMemo(() => {
    if (!user?.uid) return null
    
    const baseConstraints = [
      where('userId', '==', user.uid),
      where('dismissed', '==', false),
      orderBy('createdAt', 'desc'),
    ]
    
    return deviceId 
      ? [...baseConstraints.slice(0, 1), where('deviceId', '==', deviceId), ...baseConstraints.slice(1)]
      : baseConstraints
  }, [user?.uid, deviceId])

  // Firebase v11+ real-time alert monitoring
  useEffect(() => {
    if (!queryConfig) {
      setAlerts([])
      setLoading(false)
      return
    }

    let isMounted = true
    const alertQuery = query(collection(db, 'deviceAlerts'), ...queryConfig)

    const unsubscribe = onSnapshot(
      alertQuery,
      (snapshot) => {
        if (!isMounted) return
        
        const alertList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate?.() || new Date(),
        }))
        
        setAlerts(alertList)
        setLoading(false)
      },
      (error) => {
        if (!isMounted) return
        
        console.error('Device alerts monitoring error:', {
          deviceId,
          userId: user?.uid,
          error: error.message,
        })
        setAlerts([])
        setLoading(false)
      },
    )

    return () => {
      isMounted = false
      unsubscribe()
    }
  }, [queryConfig, deviceId, user?.uid])

  // Firebase v11+ optimized alert creation with server timestamps
  const createAlert = useCallback(async (alertData) => {
    if (!user?.uid || !alertData.deviceId) return
    
    try {
      const alert = {
        userId: user.uid,
        deviceId: alertData.deviceId,
        type: alertData.type, // 'connectivity', 'performance', 'health', 'fault'
        severity: alertData.severity, // 'low', 'medium', 'high', 'critical'
        title: alertData.title,
        message: alertData.message,
        source: alertData.source || 'system',
        data: alertData.data || null,
        read: false,
        dismissed: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }

      await addDoc(collection(db, 'deviceAlerts'), alert)
    } catch (error) {
      console.error('Alert creation failed:', {
        deviceId: alertData.deviceId,
        type: alertData.type,
        error: error.message,
      })
    }
  }, [user?.uid])

  // Mark single alert as read
  const markAsRead = useCallback(async (alertId) => {
    if (!alertId) return
    
    try {
      await updateDoc(doc(db, 'deviceAlerts', alertId), {
        read: true,
        updatedAt: serverTimestamp(),
      })
    } catch (error) {
      console.error('Mark alert as read failed:', { alertId, error: error.message })
    }
  }, [])

  // Dismiss alert
  const dismissAlert = useCallback(async (alertId) => {
    if (!alertId) return
    
    try {
      await updateDoc(doc(db, 'deviceAlerts', alertId), {
        dismissed: true,
        updatedAt: serverTimestamp(),
      })
    } catch (error) {
      console.error('Dismiss alert failed:', { alertId, error: error.message })
    }
  }, [])

  // Firebase v11+ batch operation for marking all alerts as read
  const markAllAsRead = useCallback(async () => {
    const unreadAlerts = alerts.filter(alert => !alert.read)
    if (unreadAlerts.length === 0) return
    
    try {
      const batch = writeBatch(db)
      const timestamp = serverTimestamp()
      
      unreadAlerts.forEach(alert => {
        const alertRef = doc(db, 'deviceAlerts', alert.id)
        batch.update(alertRef, { read: true, updatedAt: timestamp })
      })
      
      await batch.commit()
    } catch (error) {
      console.error('Mark all alerts as read failed:', {
        count: unreadAlerts.length,
        error: error.message,
      })
    }
  }, [alerts])

  // Connectivity alert helper with optimized time calculations
  const createConnectivityAlert = useCallback((deviceId, status, lastSeen) => {
    if (!deviceId) return
    
    const now = Date.now()
    const lastSeenTime = lastSeen ? new Date(lastSeen).getTime() : now
    const hoursOffline = Math.floor((now - lastSeenTime) / 3_600_000) // 1 hour in ms
    
    const [severity, title, message] = hoursOffline > 24 
      ? ['high', 'Device Offline Extended', `Device offline for ${hoursOffline} hours`]
      : hoursOffline > 1
      ? ['medium', 'Device Offline', `Device offline for ${hoursOffline} hour${hoursOffline > 1 ? 's' : ''}`]
      : ['medium', 'Device Offline', 'Device has lost connection']

    return createAlert({
      deviceId,
      type: 'connectivity',
      severity,
      title,
      message,
      source: 'system',
      data: { status, lastSeen, hoursOffline },
    })
  }, [createAlert])

  // Performance alert helper with severity mapping
  const createPerformanceAlert = useCallback((deviceId, metric, value, threshold) => {
    if (!deviceId || typeof value !== 'number') return
    
    const severityThresholds = {
      cpu: [[90, 'high'], [80, 'medium'], [0, 'low']],
      memory: [[95, 'critical'], [85, 'high'], [0, 'medium']],
      temperature: [[80, 'critical'], [70, 'high'], [0, 'medium']],
    }
    
    const thresholds = severityThresholds[metric] || [[80, 'medium'], [0, 'low']]
    const severity = thresholds.find(([threshold]) => value > threshold)?.[1] || 'low'
    const metricTitle = metric.charAt(0).toUpperCase() + metric.slice(1)

    return createAlert({
      deviceId,
      type: 'performance',
      severity,
      title: `High ${metricTitle} Usage`,
      message: `${metricTitle} usage is ${value}%, exceeding threshold of ${threshold}%`,
      source: 'device',
      data: { metric, value, threshold },
    })
  }, [createAlert])

  // Health check alert helper
  const createHealthAlert = useCallback((deviceId, healthChecks) => {
    if (!deviceId || !healthChecks) return
    
    const failedChecks = Object.entries(healthChecks)
      .filter(([, status]) => !status)
      .map(([check]) => check)

    if (failedChecks.length === 0) return

    return createAlert({
      deviceId,
      type: 'health',
      severity: failedChecks.length > 2 ? 'high' : 'medium',
      title: 'System Health Issues',
      message: `Failed health checks: ${failedChecks.join(', ')}`,
      source: 'device',
      data: { failedChecks, healthChecks },
    })
  }, [createAlert])

  return {
    alerts,
    loading,
    unreadCount,
    createAlert,
    markAsRead,
    dismissAlert,
    markAllAsRead,
    // Specialized alert creation helpers
    createConnectivityAlert,
    createPerformanceAlert,
    createHealthAlert,
  }
}
